import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminMcpConnection } from '@gg/shared';
import { Button, Card, ConfirmDialog, Table, Td, Th } from '@/components/ui';
import { formatDateTime } from '@/lib/dates';
import { useAdminMcpConnections, useRevokeMcp } from '@/lib/queries';

/**
 * MCP access (issue #47), on the settings page: the connector URL to give Claude, the AI
 * clients connected to the back office, and "Revoke MCP access" for one or all. Audited by the
 * API. Signing out of the back office also revokes that organiser's connections.
 */
export function AdminMcpAccess() {
  const { t } = useTranslation();
  const list = useAdminMcpConnections();
  const revoke = useRevokeMcp();
  // `null` = every connection, a connection = that one.
  const [pending, setPending] = useState<AdminMcpConnection | null | undefined>(undefined);
  const [done, setDone] = useState<number | null>(null);
  const connections = list.data?.connections ?? [];

  const confirm = async () => {
    if (pending === undefined) return;
    try {
      const { revoked } = await revoke.mutateAsync(pending?.id ?? null);
      setDone(revoked);
    } catch {
      // Shown through revoke.isError.
    }
    setPending(undefined);
  };

  return (
    <Card className="gap-4!">
      <div className="space-y-2">
        <h2 className="text-lg font-bold">{t('admin.mcp.title')}</h2>
        <p className="m-0 text-sm text-ink-muted">{t('admin.mcp.intro')}</p>
      </div>
      {list.data && (
        <p className="m-0 text-sm">
          {t('admin.mcp.url')}{' '}
          <code className="rounded-control bg-surface px-1.5 py-0.5 break-all">
            {list.data.mcpUrl}
          </code>
        </p>
      )}

      {list.isLoading && <p className="m-0 text-ink-muted">…</p>}
      {list.isError && (
        <p role="alert" className="m-0 text-sm text-danger">
          {t('admin.mcp.loadError')}
        </p>
      )}
      {list.data && connections.length === 0 && (
        <p className="m-0 text-sm text-ink-muted">{t('admin.mcp.empty')}</p>
      )}
      {connections.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th>{t('admin.mcp.col.user')}</Th>
              <Th>{t('admin.mcp.col.client')}</Th>
              <Th>{t('admin.mcp.col.created')}</Th>
              <Th>{t('admin.mcp.col.lastUsed')}</Th>
              <Th>{t('admin.mcp.col.expires')}</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {connections.map((c) => (
              <tr key={c.id}>
                <Td>{c.user.email}</Td>
                <Td>
                  {c.clientName}
                  {c.redirectHost && (
                    <span className="block text-helper text-ink-muted">{c.redirectHost}</span>
                  )}
                </Td>
                <Td>{formatDateTime(c.createdAt)}</Td>
                <Td>{c.lastUsedAt ? formatDateTime(c.lastUsedAt) : t('admin.mcp.never')}</Td>
                <Td>{formatDateTime(c.expiresAt)}</Td>
                <Td className="text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="md"
                    className="w-auto!"
                    onClick={() => setPending(c)}
                  >
                    {t('admin.mcp.revoke')}
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <Button
          type="button"
          variant="secondary"
          size="md"
          className="w-auto!"
          disabled={!connections.length}
          onClick={() => setPending(null)}
        >
          {t('admin.mcp.revokeAll')}
        </Button>
        {done !== null && (
          <span role="status" className="text-sm text-ink-muted">
            {t('admin.mcp.revoked', { count: done })}
          </span>
        )}
        {revoke.isError && (
          <span role="alert" className="text-sm text-danger">
            {t('admin.mcp.revokeError')}
          </span>
        )}
      </div>

      <ConfirmDialog
        open={pending !== undefined}
        title={t(pending ? 'admin.mcp.confirm.oneTitle' : 'admin.mcp.confirm.allTitle')}
        confirmLabel={t('admin.mcp.revoke')}
        cancelLabel={t('admin.confirm.cancel')}
        loading={revoke.isPending}
        onConfirm={confirm}
        onCancel={() => setPending(undefined)}
      >
        <p className="m-0">
          {pending
            ? t('admin.mcp.confirm.oneBody', {
                client: pending.clientName,
                email: pending.user.email,
              })
            : t('admin.mcp.confirm.allBody')}
        </p>
      </ConfirmDialog>
    </Card>
  );
}
